import { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import '../styles/deposit-modal.css'
import { apiErrorMessage } from '../utils/apiErrorMessage'

/**
 * CreditModal — the "C" (Credit) action on the Account List.
 *
 * Layout follows the reference (Old Credit / New Credit / Transaction Password).
 * There is only one input here: the new credit. Old Credit wallet ki NAYI `cr`
 * key se aata hai, aur jo number type hota hai wahi seedha
 * /admin/wallet/set-credit-limit ko jaata hai (delta nahi, poori nayi value).
 *
 * The Transaction Password is required because the route sits behind the
 * verifyTransactionPassword middleware on the server — without it the request
 * returns 401.
 *
 * @param {boolean}  props.show
 * @param {Function} props.onHide
 * @param {Object}   props.userData - target account (list row)
 * @param {Function} props.onSubmit - async ({ credit, prevCredit, transactionPassword })
 *                                    → API result
 */
function CreditModal({ show, onHide, userData = null, onSubmit }) {
  const [formData, setFormData] = useState({ amount: '', transactionPassword: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // The modal stays mounted in the parent — clear the form on every open.
  useEffect(() => {
    if (!show) return
    setFormData({ amount: '', transactionPassword: '' })
    setError('')
    setBusy(false)
  }, [show])

  const num = (v) => {
    const n = parseFloat(v)
    return Number.isFinite(n) ? n : 0
  }
  const round2 = (n) => Math.round(n * 100) / 100
  const fmt = (n) => (Number.isFinite(n) ? String(round2(n)) : '')

  // Old Credit = wallet ki nayi `cr` key (set-credit-limit isi ko set karta hai).
  const prevCredit = num(userData?.cr)

  const typed = String(formData.amount).trim()
  const nextCredit = num(formData.amount)
  const delta = typed === '' ? 0 : round2(nextCredit - prevCredit)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (error) setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (typed === '' || !Number.isFinite(parseFloat(typed))) {
      setError('Enter a valid credit amount')
      return
    }
    if (nextCredit < 0) {
      setError('Credit cannot be negative')
      return
    }
    if (delta === 0) {
      setError(`Credit is already ${fmt(prevCredit)} — nothing to change`)
      return
    }
    if (!formData.transactionPassword) {
      setError('Transaction Password required')
      return
    }

    setBusy(true)
    try {
      const res = await onSubmit?.({
        credit: round2(nextCredit),
        prevCredit,
        transactionPassword: formData.transactionPassword
      })
      if (res?.success) {
        setFormData({ amount: '', transactionPassword: '' })
        onHide()
      } else {
        setError(res?.message || res?.error || 'Something went wrong')
      }
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const handleClose = () => {
    if (busy) return
    setFormData({ amount: '', transactionPassword: '' })
    setError('')
    onHide()
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="modal-md"
    >
      {/* reference renders <header class="modal-header"> */}
      <header className="modal-header">
        <Modal.Title as="h5">Credit</Modal.Title>
        <button
          type="button"
          className="close text-white"
          onClick={handleClose}
          aria-label="Close"
        >
          ×
        </button>
      </header>
      <Modal.Body className="credit-mdl">
        <form onSubmit={handleSubmit} method="post" noValidate>
          {/* Reference layout: Old Credit (read-only) → New Credit → Transaction Password.
              The number typed is the NEW credit; deposit/withdraw of the difference is
              derived in handleSubmit, unchanged. */}
          <div className="form-group row">
            <label className="col-form-label col-4">Old Credit</label>
            <div className="col-8">
              <input
                placeholder="Old Credit"
                type="text"
                readOnly
                name="oldCredit"
                className="form-control txt-right"
                value={fmt(prevCredit)}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-form-label col-4">New Credit</label>
            <div className="col-8 form-group-feedback form-group-feedback-right">
              <input
                type="text"
                inputMode="decimal"
                name="amount"
                className="form-control txt-right"
                value={formData.amount}
                onChange={handleChange}
                autoFocus
                aria-required="true"
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-form-label col-4">Transaction Password</label>
            <div className="col-8 form-group-feedback form-group-feedback-right">
              <input
                name="transactionPassword"
                type="password"
                className="form-control"
                value={formData.transactionPassword}
                onChange={handleChange}
                aria-required="true"
              />
            </div>
          </div>

          {error && (
            <div className="form-group row">
              <div className="col-12 text-right text-danger">{error}</div>
            </div>
          )}

          <div className="form-group row">
            <div className="col-12 text-right">
              <button
                type="button"
                className="btn btn-back"
                onClick={handleClose}
                disabled={busy}
              >
                <i className="fas fa-undo mr-1"></i>
                Back
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy}
              >
                submit
                <i className="fas fa-sign-in-alt ml-1"></i>
              </button>
            </div>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  )
}

export default CreditModal
